import {
  AggregatorDatum,
  computeRatioAggregation,
  isKnownMetric,
  MASTER_METRIC_MAP,
  MetricAggregationSpec
} from '@blisspointmedia/bpm-types/dist/Metrics';
import { ColumnMetaData } from '@Components/MetricsTable/metrics-table-utils';

export type RatioRecipe = Extract<MetricAggregationSpec, { kind: 'ratio' }>;
type Aggregator = (datum: AggregatorDatum) => number | null;

/** The registry's ratio recipe for a metric; undefined for opaque metrics, base columns and
 *  names outside the registry. */
export const getRatioRecipe = (name: string): RatioRecipe | undefined => {
  if (!Object.hasOwn(MASTER_METRIC_MAP, name) || !isKnownMetric(name)) {
    return undefined;
  }
  const { aggregation } = MASTER_METRIC_MAP[name];
  return aggregation.kind === 'ratio' ? aggregation : undefined;
};

/** Client math for a ratio metric, generated from its registry recipe. `prefix` reads scoped
 *  component columns (e.g. Pacing's `toward_spend`). */
export const ratioAggregator = (name: string, prefix = ''): Aggregator | undefined => {
  const recipe = getRatioRecipe(name);
  if (!recipe) {
    return undefined;
  }
  const { numerator, denominator, multiplier } = recipe;
  return datum =>
    computeRatioAggregation(
      {
        [numerator]: datum[`${prefix}${numerator}`],
        [denominator]: datum[`${prefix}${denominator}`]
      },
      numerator,
      denominator,
      multiplier
    );
};

/** Swaps each ratio metric's `aggregator` for the recipe-generated one. Entries without a ratio
 *  recipe (base columns, opaque or FE-local metrics) are left as-is. */
export const withRatioAggregators = <K extends string>(
  map: Partial<Record<K, ColumnMetaData>>
): Partial<Record<K, ColumnMetaData>> => {
  const result = { ...map };
  for (const key in result) {
    if (!Object.hasOwn(result, key)) continue;
    const entry = result[key];
    const aggregator = ratioAggregator(key);
    if (entry && aggregator) {
      result[key] = { ...entry, aggregator };
    }
  }
  return result;
};
